import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import UnfoldLess from "@mui/icons-material/UnfoldLess";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { SuggestedField } from "components/ArtificialIntelligence/SuggestedField";
import { SuggestedStatePicker } from "components/ArtificialIntelligence/SuggestedStatePicker";
import { SuggestedTextField } from "components/ArtificialIntelligence/SuggestedTextField";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { RcraInfoLink } from "components/RcraInfoLink";
import { safeParseFloat, safeParseInt } from "encamp-shared/src/tierii/utilities";
import { useManifestViewsV2 } from "hooks/useManifestViewsV2";
import { useState } from "react";

import type { useManifestReviewSection } from "../hooks/useManifestReviewSection";

import { GeneratorSiteAddressFields } from "./GeneratorSiteAddressFields";

type ManifestDetailSectionProps = {
  manifest: ReturnType<typeof useManifestReviewSection>;
};

type ManifestSection = ReturnType<typeof useManifestReviewSection>;

/**
 * Deep-link beneath an EPA ID field to look that exact site up in RCRAInfo.
 * Hidden until the field holds a value — an empty field has nothing to look up,
 * and the section's general "Search RCRAInfo HWIP" link covers look-ups by name.
 *
 * It runs a site search for the ID (not a direct record link), so an
 * unrecognized ID shows a graceful "no site found" results page. Confirming an
 * ID against RCRAInfo handler data in-app is a separate, server-side enhancement.
 */
function EpaIdLookup({ epaId }: { epaId: unknown }) {
  if (typeof epaId !== "string" || epaId.trim() === "") return null;
  return (
    <Box sx={{ pl: 0.5 }}>
      <RcraInfoLink epaId={epaId} />
    </Box>
  );
}

export function ManifestDetailSection({ manifest }: ManifestDetailSectionProps) {
  // TX Solid Waste Registration is Texas-only and frequently absent even there,
  // so the field stays hidden unless this looks like a Texas generator. We treat
  // it as Texas when either generator state (mailing OR site) is TX, when the
  // generator EPA ID carries a TX prefix (covers manifests whose site address is
  // "SAME"/blank), or when a SWR value is already present.
  const generatorMailState = manifest.manifestWatch("generatorMailState");
  const generatorLocationState = manifest.manifestWatch("generatorLocationState");
  const generatorIdValue = manifest.manifestWatch("generatorId");
  const txSwrValue = manifest.manifestWatch("generatorTxStateWasteRegistrationId");
  const isTxGenerator =
    generatorMailState === "TX" ||
    generatorLocationState === "TX" ||
    !!generatorIdValue?.trim().toUpperCase().startsWith("TX");
  const showTxSwr = isTxGenerator || !!txSwrValue?.trim();

  if (manifest.manifestDataLoaded) {
    return (
      <Box sx={{ p: 1 }}>
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {manifest.manifestNeedsReview ? (
              <>
                AI parsed this manifest document. Please review the parsed values{" "}
                <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                  (marked with <SparkleIcon fontSize="inherit" sx={{ verticalAlign: "middle" }} />)
                </Box>{" "}
                and confirm.
              </>
            ) : (
              "This is the raw data parsed from the manifest document. Edits here will update the waste manifest record and trigger reprocessing."
            )}{" "}
            Need to confirm an EPA ID? <RcraInfoLink />
          </Typography>

          {/* Fields follow the EPA Form 8700-22 box order, top to bottom, so a
              reviewer can check parsed values straight down the document. Box
              numbers are shown in the labels/headers. Line Items and
              Transporters stay grouped since they continue onto page 2. */}
          {manifest.facility && <DisplayOnlyField label="Facility" text={manifest.facility.name} />}

          {/* Box 1 — Generator EPA ID, with the Texas SWR alongside it for TX
              generators / when a value exists (the EPA ID grows to fill the row
              otherwise). Kept in one persistent Stack so the input never
              remounts mid-typing. A RCRAInfo lookup link sits below for manual
              confirmation, in addition to the inline validation warning. */}
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={2}>
              <SuggestedTextField
                name="generatorId"
                control={manifest.manifestControl}
                needsReview={manifest.manifestNeedsReview}
                label="1. Generator ID Number"
                size="small"
                sx={{ flex: 2, minWidth: 0 }}
              />
              {showTxSwr && (
                <SuggestedTextField
                  name="generatorTxStateWasteRegistrationId"
                  control={manifest.manifestControl}
                  needsReview={manifest.manifestNeedsReview}
                  label="1. TX SWR Number"
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                  helperText="Texas only (optional)"
                />
              )}
            </Stack>
            <EpaIdLookup epaId={manifest.manifestWatch("generatorId")} />
          </Stack>

          {/* Box 4 */}
          <SuggestedTextField
            name="manifestTrackingNumber"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="4. Manifest Tracking Number"
            size="small"
            fullWidth
          />
          {manifest.trackingNumberChanged && (
            <Alert severity="warning" variant="outlined">
              Changing the tracking number will move this manifest data to a different processed
              manifest record.
            </Alert>
          )}

          {/* Box 5 */}
          <Typography variant="subtitle1" fontWeight="bold">
            5. Generator
          </Typography>
          <SuggestedTextField
            name="generatorName"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Name"
            size="small"
            fullWidth
          />
          <SuggestedTextField
            name="generatorMailStreet1"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Mailing Street"
            size="small"
            fullWidth
          />
          <SuggestedTextField
            name="generatorMailStreet2"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Mailing Street 2"
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorMailCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              sx={{ flex: 2, minWidth: 0 }}
            />
            <SuggestedStatePicker
              name="generatorMailState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              sx={{ flex: 1, minWidth: 56 }}
            />
            <SuggestedTextField
              name="generatorMailZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ flex: 1.5, minWidth: 0 }}
            />
          </Stack>
          <GeneratorSiteAddressFields
            control={manifest.manifestControl}
            setValue={manifest.manifestSetValue}
            needsReview={manifest.manifestNeedsReview}
            siteInitiallyBlank={manifest.generatorSiteAddressInitiallyBlank}
            mailingPopulated={manifest.generatorMailingAddressPresent}
          />

          {/* Boxes 6 & 7 (+ continuation) */}
          <TransportersSection manifest={manifest} />

          {/* Box 8 */}
          <Typography variant="subtitle1" fontWeight="bold">
            8. Designated Facility
          </Typography>
          <SuggestedTextField
            name="desFacilityName"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Name"
            size="small"
            fullWidth
          />
          <Stack spacing={0.5}>
            <SuggestedTextField
              name="desFacilityId"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="EPA ID"
              size="small"
              fullWidth
            />
            <EpaIdLookup epaId={manifest.manifestWatch("desFacilityId")} />
          </Stack>
          <SuggestedTextField
            name="desFacLocationStreet1"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Street"
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="desFacLocationCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              sx={{ flex: 2, minWidth: 0 }}
            />
            <SuggestedStatePicker
              name="desFacLocationState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              sx={{ flex: 1, minWidth: 56 }}
            />
            <SuggestedTextField
              name="desFacLocationZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ flex: 1.5, minWidth: 0 }}
            />
          </Stack>

          {/* Boxes 9–13 (+ continuation) */}
          <WasteLinesSection manifest={manifest} />

          {/* Box 15 — Generator's certification date. */}
          <SuggestedTextField
            name="shippedDate"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="15. Date Shipped"
            type="date"
            size="small"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />

          {/* Box 18 — Discrepancy Indication Space. */}
          <Box18Section manifest={manifest} />

          {/* Box 20 — Designated facility owner/operator certification date. */}
          <SuggestedTextField
            name="receivedDate"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="20. Date Received"
            type="date"
            size="small"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Box>
    );
  }

  if (manifest.manifestLoading) {
    return <Skeleton variant="rectangular" height={200} sx={{ my: 2, gridColumn: "1 / -1" }} />;
  }

  return (
    <Alert severity="info" variant="outlined" sx={{ my: 2, gridColumn: "1 / -1" }}>
      This document has been classified as a waste manifest. Manifest data will be parsed after you
      confirm, and you can review it from the Waste Manifests page.
    </Alert>
  );
}

function WasteLinesSection({ manifest }: { manifest: ManifestSection }) {
  const fieldArray = manifest.wasteLineFieldArray;

  // Lazy init: first row expanded by default. Storing wasteLineNumber (not
  // field.id) so the "Add Waste Line" handler can update the Set in one go
  // — it knows the new wasteLineNumber synchronously.
  const [expandedLineNumbers, setExpandedLineNumbers] = useState<Set<string>>(() => {
    const first = fieldArray.fields[0]?.wasteLineNumber;
    return new Set(first ? [first] : []);
  });

  const setLineExpanded = (lineNumber: string, expanded: boolean) =>
    setExpandedLineNumbers((prev) => {
      const next = new Set(prev);
      if (expanded) next.add(lineNumber);
      else next.delete(lineNumber);
      return next;
    });

  const allExpanded =
    fieldArray.fields.length > 0 &&
    fieldArray.fields.every((f) => expandedLineNumbers.has(f.wasteLineNumber));

  const toggleAll = () =>
    setExpandedLineNumbers(
      allExpanded ? new Set() : new Set(fieldArray.fields.map((f) => f.wasteLineNumber))
    );

  const handleAddLine = () => {
    const nextLineNumber = String(
      Math.max(0, ...fieldArray.fields.map((f) => safeParseInt(f.wasteLineNumber) ?? 0)) + 1
    );
    fieldArray.append({
      wasteLineNumber: nextLineNumber,
      dotHazardous: false,
      dotIdNumberDescription: "",
      dotPrintedInformation: "",
      nonHazWasteDescription: "",
      containerNumber: null,
      containerTypeCode: "",
      quantity: null,
      qtyUnitOfMeasureCode: "",
      managementMethodCode: "",
      federalWasteCodes: "",
      stateWasteCodes: "",
    });
    // Newly-added line starts expanded so the user can fill it in.
    setLineExpanded(nextLineNumber, true);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          9–13. Line Items ({fieldArray.fields.length})
        </Typography>
        {fieldArray.fields.length > 1 && (
          <Button
            size="small"
            variant="text"
            startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
            onClick={toggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Stack>
      {fieldArray.fields.map((field, index) => (
        <WasteLineRow
          key={field.id}
          field={field}
          index={index}
          manifest={manifest}
          expanded={expandedLineNumbers.has(field.wasteLineNumber)}
          onToggle={(open) => setLineExpanded(field.wasteLineNumber, open)}
        />
      ))}
      <Button size="small" startIcon={<Add />} onClick={handleAddLine}>
        Add Waste Line
      </Button>
    </>
  );
}

type WasteLineRowProps = {
  field: { id: string; wasteLineNumber: string };
  index: number;
  manifest: ManifestSection;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
};

// EPA Form 8700-22 holds four waste lines; the fifth onward continue on the
// 8700-22A continuation sheet, which numbers the same fields differently.
const MAIN_FORM_WASTE_LINE_COUNT = 4;
const MAIN_FORM_BOXES = {
  hm: "9a",
  description: "9b",
  containers: "10",
  quantity: "11",
  unit: "12",
  wasteCodes: "13",
  managementMethod: "19",
};
const CONTINUATION_BOXES = {
  hm: "27a",
  description: "27b",
  containers: "28",
  quantity: "29",
  unit: "30",
  wasteCodes: "31",
  managementMethod: "36",
};

function WasteLineRow({ field, index, manifest, expanded, onToggle }: WasteLineRowProps) {
  const lineValues = manifest.manifestWatch(`wasteLines.${index}`);
  const isHazardous = lineValues.dotHazardous;
  const description = isHazardous
    ? lineValues.dotPrintedInformation
    : lineValues.nonHazWasteDescription;
  // A line's box numbers depend on which physical page it lands on.
  const box = index < MAIN_FORM_WASTE_LINE_COUNT ? MAIN_FORM_BOXES : CONTINUATION_BOXES;

  return (
    <Accordion expanded={expanded} onChange={(_, isOpen) => onToggle(isOpen)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Line {field.wasteLineNumber}
          </Typography>
          {isHazardous && (
            <WarningAmber
              color="warning"
              sx={{ fontSize: 18, flexShrink: 0 }}
              titleAccess="Hazardous"
            />
          )}
          {description && (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
              {" — "}
              {description}
            </Typography>
          )}
          {!description && <Box flex={1} />}
          <IconButton
            component="div"
            role="button"
            tabIndex={0}
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              manifest.onDeleteWasteLine(field.wasteLineNumber);
              manifest.wasteLineFieldArray.remove(index);
            }}
            title="Remove waste line"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <SuggestedField
              name={`wasteLines.${index}.dotHazardous`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleIcon }) => (
                <Stack direction="row" alignItems="center">
                  <FormControlLabel
                    control={<Checkbox checked={!!f.value} onChange={f.onChange} />}
                    label={`${box.hm}. DOT Hazardous`}
                  />
                  {isSuggested && sparkleIcon}
                </Stack>
              )}
            </SuggestedField>
            <SuggestedTextField
              name={`wasteLines.${index}.dotIdNumberDescription`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label={`${box.description}. DOT ID / Description`}
              size="small"
              fullWidth
            />
          </Stack>
          <SuggestedTextField
            name={`wasteLines.${index}.dotPrintedInformation`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box.description}. DOT Printed Information`}
            size="small"
            fullWidth
            multiline
            rows={3}
          />
          <SuggestedTextField
            name={`wasteLines.${index}.nonHazWasteDescription`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box.description}. Non-Haz Waste Description`}
            size="small"
            fullWidth
            multiline
            rows={3}
          />
          <Stack direction="row" spacing={2}>
            <SuggestedField
              name={`wasteLines.${index}.containerNumber`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleAdornment }) => (
                <TextField
                  {...f}
                  value={f.value ?? ""}
                  onChange={(e) =>
                    f.onChange(e.target.value === "" ? null : safeParseInt(e.target.value))
                  }
                  label={`${box.containers}. No. of Containers`}
                  type="number"
                  size="small"
                  sx={{ flex: 1, minWidth: 90 }}
                  slotProps={{
                    input: {
                      startAdornment: isSuggested ? sparkleAdornment : undefined,
                    },
                  }}
                />
              )}
            </SuggestedField>
            <SuggestedTextField
              name={`wasteLines.${index}.containerTypeCode`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label={`${box.containers}. Container Type`}
              size="small"
              sx={{ flex: 1, minWidth: 110 }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedField
              name={`wasteLines.${index}.quantity`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleAdornment }) => (
                <TextField
                  {...f}
                  value={f.value ?? ""}
                  onChange={(e) =>
                    f.onChange(e.target.value === "" ? null : safeParseFloat(e.target.value))
                  }
                  label={`${box.quantity}. Total Quantity`}
                  type="number"
                  size="small"
                  sx={{ flex: 1, minWidth: 90 }}
                  slotProps={{
                    input: {
                      startAdornment: isSuggested ? sparkleAdornment : undefined,
                    },
                  }}
                />
              )}
            </SuggestedField>
            <SuggestedTextField
              name={`wasteLines.${index}.qtyUnitOfMeasureCode`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label={`${box.unit}. Unit Wt./Vol.`}
              size="small"
              sx={{ flex: 1, minWidth: 90 }}
            />
          </Stack>
          <SuggestedTextField
            name={`wasteLines.${index}.federalWasteCodes`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box.wasteCodes}. Federal Waste Codes`}
            size="small"
            fullWidth
            helperText="Comma-separated"
          />
          <SuggestedTextField
            name={`wasteLines.${index}.stateWasteCodes`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box.wasteCodes}. State Waste Codes`}
            size="small"
            fullWidth
            helperText="Comma-separated"
          />
          {/* Management method codes sit at the bottom of the form, so they
              come after the waste codes within each line. */}
          <SuggestedTextField
            name={`wasteLines.${index}.managementMethodCode`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box.managementMethod}. Management Method Code`}
            size="small"
            fullWidth
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function TransportersSection({ manifest }: { manifest: ManifestSection }) {
  const fieldArray = manifest.transporterFieldArray;

  // Each transporter is an independently collapsible accordion, collapsed by
  // default — the summary shows EPA ID + name, so reviewers can scan all
  // transporters at a glance and expand only when they need to edit one.
  const [expandedLineNumbers, setExpandedLineNumbers] = useState<Set<string>>(() => new Set());

  const setLineExpanded = (lineNumber: string, expanded: boolean) =>
    setExpandedLineNumbers((prev) => {
      const next = new Set(prev);
      if (expanded) next.add(lineNumber);
      else next.delete(lineNumber);
      return next;
    });

  const allExpanded =
    fieldArray.fields.length > 0 &&
    fieldArray.fields.every((f) => expandedLineNumbers.has(f.transporterLineNumber));

  const toggleAll = () =>
    setExpandedLineNumbers(
      allExpanded ? new Set() : new Set(fieldArray.fields.map((f) => f.transporterLineNumber))
    );

  const handleAddTransporter = () => {
    const nextLineNumber = String(
      Math.max(0, ...fieldArray.fields.map((f) => safeParseInt(f.transporterLineNumber) ?? 0)) + 1
    );
    fieldArray.append({
      transporterLineNumber: nextLineNumber,
      transporterId: "",
      transporterName: "",
    });
    // Newly-added transporter starts expanded so the user can fill it in.
    setLineExpanded(nextLineNumber, true);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          6 &amp; 7. Transporters ({fieldArray.fields.length})
        </Typography>
        {fieldArray.fields.length > 1 && (
          <Button
            size="small"
            variant="text"
            startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
            onClick={toggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Stack>
      {fieldArray.fields.map((field, index) => (
        <TransporterRow
          key={field.id}
          field={field}
          index={index}
          manifest={manifest}
          expanded={expandedLineNumbers.has(field.transporterLineNumber)}
          onToggle={(open) => setLineExpanded(field.transporterLineNumber, open)}
        />
      ))}
      <Button size="small" startIcon={<Add />} onClick={handleAddTransporter}>
        Add Transporter
      </Button>
    </>
  );
}

type TransporterRowProps = {
  field: { id: string; transporterLineNumber: string };
  index: number;
  manifest: ManifestSection;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
};

// EPA Form 8700-22 lists two transporters (Items 6 & 7); additional transporters
// go on the 8700-22A continuation sheet, two per sheet (Items 25 & 26).
const MAIN_FORM_TRANSPORTER_COUNT = 2;
function transporterBox(index: number): string {
  if (index === 0) return "6";
  if (index === 1) return "7";
  return (index - MAIN_FORM_TRANSPORTER_COUNT) % 2 === 0 ? "25" : "26";
}

function TransporterRow({ field, index, manifest, expanded, onToggle }: TransporterRowProps) {
  const epaId = manifest.manifestWatch(`transporters.${index}.transporterId`);
  const name = manifest.manifestWatch(`transporters.${index}.transporterName`);
  // Name first, then EPA ID, mirroring the manifest form's field order.
  const summary = [name, epaId].filter(Boolean).join(" · ");
  const box = transporterBox(index);

  return (
    <Accordion expanded={expanded} onChange={(_, isOpen) => onToggle(isOpen)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Transporter {field.transporterLineNumber}
          </Typography>
          {summary ? (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
              {" — "}
              {summary}
            </Typography>
          ) : (
            <Box flex={1} />
          )}
          <IconButton
            component="div"
            role="button"
            tabIndex={0}
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              manifest.onDeleteTransporter(field.transporterLineNumber);
              manifest.transporterFieldArray.remove(index);
            }}
            title="Remove transporter"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <SuggestedTextField
            name={`transporters.${index}.transporterName`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box}. Company Name`}
            size="small"
            fullWidth
          />
          <Stack spacing={0.5}>
            <SuggestedTextField
              name={`transporters.${index}.transporterId`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label={`${box}. EPA ID`}
              size="small"
              fullWidth
            />
            <EpaIdLookup epaId={epaId} />
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// Box 18 is filled out by the receiving TSDF when there's a discrepancy
// between what the generator shipped and what arrived (corrected quantities,
// wrong waste type), or when material is partially/fully rejected, or when
// only a residue remains. Most manifests leave this blank — collapse by
// default unless one of the fields already has content (OCR or prior edit).
function Box18Section({ manifest }: { manifest: ManifestSection }) {
  // Box 18 shipped with the manifest-views-v2 surface; this dialog is also
  // reachable from the ungated customer Documents area, so gate the section on
  // the rollout flag (staff resolve it on via staff override).
  const showManifestViewsV2 = useManifestViewsV2();
  const discrepancyComments = manifest.manifestWatch("discrepancyComments");
  const residueComments = manifest.manifestWatch("residueComments");
  const rejectionComments = manifest.manifestWatch("rejectionComments");

  const filledCount = [discrepancyComments, residueComments, rejectionComments].filter(
    (v) => v && v.trim().length > 0
  ).length;

  // react-hook-form applies the `values` prop in a useEffect, so at mount the
  // watched fields read as empty even when OCR populated them. Storing
  // `expanded` from a lazy-init `filledCount > 0` would freeze to false before
  // hydration. Instead, persist only the user's manual toggle and derive
  // `expanded` live so it tracks `filledCount` once values land.
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const expanded = userExpanded ?? filledCount > 0;

  if (!showManifestViewsV2) {
    return null;
  }

  return (
    <Accordion expanded={expanded} onChange={(_, isOpen) => setUserExpanded(isOpen)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ flexShrink: 0 }}>
            18a. Discrepancy Indications
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
            {filledCount > 0
              ? // 0 is routed to "(none)" below, so the s/no-s ternary only handles 1 vs. ≥2.
                ` — ${filledCount} field${filledCount === 1 ? "" : "s"} populated`
              : "(none)"}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Alert severity="info" variant="outlined">
            Box 18 is completed by the receiving facility (TSDF). On the paper form each row has a
            checkbox plus a narrative — capture the narrative in the matching field below; leave
            blank if the box is unchecked
          </Alert>
          <SuggestedTextField
            name="discrepancyComments"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="18a. Quantity / type discrepancy"
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <SuggestedTextField
            name="residueComments"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="18a. Residue"
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <SuggestedTextField
            name="rejectionComments"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="18a. Rejection"
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
